/**
 * StyleSettingsDialog — More Settings shell for Writing, Chat
 * Personalization, Styles, and Spelling & Grammar.
 *
 * Layout mirrors the Words to Avoid Figma (node 786:18130) and the
 * existing sudokit `TabbedDialog` pattern: a left nav rail + a right
 * scrollable content pane. Open/close state lives in sudowriteStore so
 * chat, rewrite, grammar, and SettingsPanel entry points share one
 * instance and can deep-link to a category (plus optional Advanced focus).
 */

import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { Dialog, DialogContent } from "src/sudokit/components/Dialog";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { cn } from "src/lib/cn";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { LanguageSection } from "./sections/LanguageSection";
import { DictionarySection } from "./sections/DictionarySection";
import { IgnoredRulesSection } from "./sections/IgnoredRulesSection";
import { StylesManagerSection } from "./sections/StylesManagerSection";
import { WritingSettingsSection } from "./sections/WritingSettingsSection";
import { ChatPersonalizationSection } from "./sections/ChatPersonalizationSection";
import type { MoreSettingsCategory } from "./types";

type CategoryId = MoreSettingsCategory;

interface CategoryDef {
  id: CategoryId;
  label: string;
  iconSrc?: string;
  render: (props: { close: () => void; active: boolean; focusAdvanced: boolean }) => JSX.Element;
}

interface StyleSettingsSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose: () => void;
  active?: boolean;
  defaultActiveCategory?: CategoryId;
}

function SpellingAndGrammarPage() {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <h4 className="m-0 font-inter font-semibold text-base text-app-grey-0">Language</h4>
        <LanguageSection />
      </div>
      <div className="flex flex-col gap-3">
        <h4 className="m-0 font-inter font-semibold text-base text-app-grey-0">Dictionary</h4>
        <DictionarySection />
      </div>
      <div className="flex flex-col gap-3">
        <h4 className="m-0 font-inter font-semibold text-base text-app-grey-0">Ignored grammar rules</h4>
        <IgnoredRulesSection />
      </div>
    </div>
  );
}

const CATEGORIES: CategoryDef[] = [
  {
    id: "writing",
    label: "Writing",
    iconSrc: "/assets/style-guide/settings/settings-write-24.svg",
    render: () => <WritingSettingsSection />,
  },
  {
    id: "chat-personalization",
    label: "Chat Personalization",
    iconSrc: "/assets/chat-personalization-icon.svg",
    render: ({ active, focusAdvanced }) => (
      <ChatPersonalizationSection
        active={active}
        defaultAdvancedOpen={focusAdvanced}
      />
    ),
  },
  {
    id: "styles",
    label: "Styles",
    iconSrc: "/assets/style-guide/settings/settings-style-24.svg",
    render: ({ close }) => <StylesManagerSection onClose={close} />,
  },
  {
    id: "spelling-and-grammar",
    label: "Spelling & Grammar",
    iconSrc: "/assets/style-guide/settings/settings-spellcheck-nav-24.svg",
    render: () => <SpellingAndGrammarPage />,
  },
];

function resolveActiveCategory(
  preferred: CategoryId | null | undefined,
  visibleCategories: CategoryDef[],
  fallback: CategoryId,
): CategoryId {
  if (preferred && visibleCategories.some((category) => category.id === preferred)) {
    return preferred;
  }
  if (visibleCategories.some((category) => category.id === fallback)) {
    return fallback;
  }
  return visibleCategories[0]?.id ?? "writing";
}

export function StyleSettingsSurface({
  onClose,
  active: isSurfaceActive = true,
  defaultActiveCategory = "writing",
  className,
  ...props
}: StyleSettingsSurfaceProps) {
  const requestedActiveCategory = useSudowriteStore((s: any) => s.styleSettingsActiveCategory);
  const focusAdvanced = !!useSudowriteStore((s: any) => s.styleSettingsFocusAdvanced);
  const clearStyleSettingsFocusAdvanced = useSudowriteStore(
    (s: any) => s.clearStyleSettingsFocusAdvanced,
  );
  const hasAccessToStyleGuide = useSudowriteStore((s: any) => s.subscription)?.hasAccessToStyleGuide;
  const visibleCategories = useMemo(
    () => CATEGORIES.filter((category) => category.id !== "styles" || hasAccessToStyleGuide),
    [hasAccessToStyleGuide],
  );
  const [activeId, setActiveId] = useState<CategoryId>("writing");
  const active = visibleCategories.find((c) => c.id === activeId) ?? visibleCategories[0];

  useEffect(() => {
    if (!isSurfaceActive) return;
    const preferredActiveCategory = requestedActiveCategory ?? defaultActiveCategory;
    setActiveId(resolveActiveCategory(preferredActiveCategory, visibleCategories, defaultActiveCategory));
  }, [defaultActiveCategory, isSurfaceActive, requestedActiveCategory, visibleCategories]);

  useEffect(() => {
    if (!visibleCategories.some((category) => category.id === activeId)) {
      setActiveId(visibleCategories[0]?.id ?? "writing");
    }
  }, [activeId, visibleCategories]);

  // Consume advanced deep-link focus once Chat Personalization has received it,
  // so later tab re-entry does not keep forcing Advanced open.
  useEffect(() => {
    if (!isSurfaceActive || !focusAdvanced) return;
    if (activeId !== "chat-personalization") return;
    clearStyleSettingsFocusAdvanced?.();
  }, [activeId, clearStyleSettingsFocusAdvanced, focusAdvanced, isSurfaceActive]);

  return (
    <WithReset
      {...props}
      className={cn("flex h-full flex-col !m-0 overflow-hidden", className)}
    >
      <WithReset className="relative flex h-[72px] shrink-0 items-center justify-between border-0 px-6">
        <WithReset className="ml-2 flex items-center gap-2.5">
          <SVG
            src="/assets/style-guide/settings/settings-gear-32.svg"
            className="h-8 w-8 shrink-0 text-[var(--grey-0)] [&_path]:fill-current"
            aria-hidden="true"
          />
          <WithReset as="h2" className="m-0 font-inter text-2xl font-medium leading-[29px] text-[var(--grey-0)]">
            Settings
          </WithReset>
        </WithReset>
        <button
          type="button"
          aria-label="Close settings"
          className="flex h-8 w-8 items-center justify-center border-0 bg-transparent p-0 cursor-pointer text-[var(--grey-0)] [&_svg_path]:fill-current"
          onClick={onClose}
        >
          <SVG
            src="/assets/style-guide/settings/settings-close-32.svg"
            className="h-8 w-8"
            aria-hidden="true"
          />
        </button>
        <WithReset className="absolute bottom-0 left-6 right-6 h-px bg-[var(--grey-3)]" />
      </WithReset>

      <WithReset className="flex min-h-0 flex-1">
        <WithReset className="w-[246px] shrink-0 overflow-y-auto px-6 pt-6">
          <WithReset as="nav" className="flex flex-col gap-4">
            {visibleCategories.map((category) => {
              const selected = activeId === category.id;
              return (
                <button
                  key={category.id}
                  type="button"
                  onMouseDown={() => setActiveId(category.id)}
                  aria-label={category.label}
                  aria-selected={selected}
                  className={cn(
                    "flex h-10 w-[206px] items-center gap-2 rounded-lg border-0 px-3 text-left font-inter text-base font-medium leading-[19px] cursor-pointer",
                    "whitespace-nowrap [&_svg_g]:opacity-100 [&_svg_path]:fill-current",
                    selected
                      ? "bg-[var(--selected-element-background)] text-[var(--selected-element-text)]"
                      : "bg-transparent text-[var(--grey-0)]",
                  )}
                >
                  {category.iconSrc && (
                    <SVG
                      src={category.iconSrc}
                      className="h-6 w-6 shrink-0 text-current [&_path]:fill-current"
                      aria-hidden="true"
                    />
                  )}
                  <span>{category.label}</span>
                </button>
              );
            })}
          </WithReset>
        </WithReset>

        <WithReset className={cn("min-w-0 flex-1 overflow-y-auto", active.id !== "styles" && "px-6 py-5")}>
          {active.render({
            close: onClose,
            active: isSurfaceActive && active.id === activeId,
            focusAdvanced: active.id === "chat-personalization" && focusAdvanced,
          })}
        </WithReset>
      </WithReset>
    </WithReset>
  );
}

function isReactSelectMenuEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  // react-select options always get react-select-*-option-* ids; classNamePrefix
  // menus expose __menu / __menu-list / __option (needed for menu chrome clicks).
  return Boolean(
    target.closest('[id^="react-select-"]') ||
      target.closest('[class*="__menu"]'),
  );
}

function getDismissEventTarget(event: {
  target?: EventTarget | null;
  detail?: { originalEvent?: { target?: EventTarget | null } };
}): EventTarget | null {
  return event.target ?? event.detail?.originalEvent?.target ?? null;
}

export function StyleSettingsDialog() {
  const isOpen = useSudowriteStore((s: any) => s.isStyleSettingsOpen);
  const close = useSudowriteStore((s: any) => s.closeStyleSettings);
  const hasAccessToStyleGuide = useSudowriteStore((s: any) => s.subscription)?.hasAccessToStyleGuide;

  // Read at event time so a nested Prose Mode open (store flag) is never stale
  // relative to Radix dismiss handlers attached on the parent dialog.
  const blockDismissWhileNestedModalOpen = (event: { preventDefault: () => void }) => {
    if ((useSudowriteStore.getState() as any).isStyleSettingsNestedModalOpen) {
      event.preventDefault();
    }
  };

  // Body-portaled Write react-select menus sit outside DialogContent, so Radix
  // treats menu clicks as outside interactions unless we ignore them here.
  const blockDismissWhileNestedUiOpen = (event: {
    preventDefault: () => void;
    target?: EventTarget | null;
    detail?: { originalEvent?: { target?: EventTarget | null } };
  }) => {
    if ((useSudowriteStore.getState() as any).isStyleSettingsNestedModalOpen) {
      event.preventDefault();
      return;
    }
    if (isReactSelectMenuEventTarget(getDismissEventTarget(event))) {
      event.preventDefault();
    }
  };

  return (
    <Dialog open={!!isOpen} onOpenChange={(o: boolean) => !o && close()}>
      <DialogContent
        data-style-settings-dialog
        backdropColor="always-dark"
        hideClose
        onPointerDownOutside={blockDismissWhileNestedUiOpen}
        onInteractOutside={blockDismissWhileNestedUiOpen}
        onEscapeKeyDown={blockDismissWhileNestedModalOpen}
        className={cn(
          "block p-0 max-w-none w-[760px] h-[560px]",
          "sm:rounded-[20px] bg-[var(--menu-color)] text-[var(--grey-0)]",
          "border border-solid border-[var(--grey-3)] shadow-none overflow-hidden",
          hasAccessToStyleGuide && "w-[min(1000px,calc(100vw-48px))] h-[min(900px,calc(100vh-48px))]",
          "antialiased text-left [font-optical-sizing:none]"
        )}
      >
        <StyleSettingsSurface onClose={close} active={!!isOpen} />
      </DialogContent>
    </Dialog>
  );
}

export default StyleSettingsDialog;
